import { motion } from "framer-motion";
import { ArrowRight, Award, Workflow, Lightbulb, Users, MapPin, HeartHandshake } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SectionHeading from "@/components/site/SectionHeading";
import ServiceCard from "@/components/site/ServiceCard";
import MobileCarousel from "@/components/site/MobileCarousel";
import { SERVICE_CATEGORIES, WHAT_WE_DO, WHY_CHOOSE, APPROACH_STEPS } from "@/data/services";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useWpCollection } from "@/hooks/useWpCollection";
import { Heading } from "@/data/siteContent";

const SERVICE_ICONS = ["Briefcase", "Landmark", "Mic2", "Palette", "Cpu", "Heart"];
const WHY_ICONS = [Award, Workflow, Lightbulb, Users, MapPin, HeartHandshake];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const toService = (item, i) => ({
  id: item.id || `service-${i}`,
  title: item.title,
  icon: item.icon || SERVICE_ICONS[i % SERVICE_ICONS.length],
  description: item.description || item.text || "",
  services: item.services || item.list || [],
});

const Hero = ({ s }) => (
  <section data-testid="services-hero" className="relative -mt-[80px] flex min-h-[70vh] items-center justify-center overflow-hidden lg:-mt-[78px]">
    <img
      src={s.services_hero_image}
      alt="Large-scale event setup by Wonder Events"
      className="absolute inset-0 h-full w-full object-cover"
      loading="eager"
    />
    <div className="absolute inset-0 bg-[#1F1F1F]/70" />
    <div className="relative z-10 mx-auto max-w-4xl px-5 py-24 text-center">
      <motion.span
        {...fadeUp()}
        data-testid="hero-eyebrow"
        className="inline-block rounded-full border border-white/30 bg-white/10 px-5 py-2 text-xs font-bold uppercase tracking-[0.28em] text-white backdrop-blur"
      >
        {s.services_hero_eyebrow}
      </motion.span>
      <motion.h1
        {...fadeUp(0.12)}
        className="mt-6 text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-6xl"
      >
        <Heading value={s.services_hero_heading} />
      </motion.h1>
      <motion.p {...fadeUp(0.22)} className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#E5E5E5] sm:text-lg">
        {s.services_hero_text}
      </motion.p>
      <motion.div {...fadeUp(0.32)}>
        <a
          data-testid="hero-cta-button"
          href={s.contact_url}
          className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-[#C62828] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_14px_35px_rgba(198,40,40,0.45)] transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:text-[#C62828]"
        >
          {s.services_hero_button}
          <ArrowRight size={17} strokeWidth={2.5} />
        </a>
      </motion.div>
    </div>
  </section>
);

const Intro = ({ s }) => (
  <section data-testid="intro-section" className="bg-white py-12 lg:py-16">
    <div className="mx-auto max-w-4xl px-5 text-center">
      <motion.div {...fadeUp()}>
        <h2 className="text-3xl font-extrabold tracking-tight text-[#1F1F1F] sm:text-4xl lg:text-[42px]">
          {s.intro_heading}
        </h2>
        <span className="mx-auto mt-5 block h-[4px] w-16 rounded-full bg-[#C62828]" />
        <p className="mt-6 text-base leading-relaxed text-[#666666] sm:text-lg">{s.intro_text}</p>
      </motion.div>
    </div>
  </section>
);

const ServicesGrid = ({ s, services }) => (
  <section data-testid="services-grid-section" className="bg-[#F8F8F8] py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        testid="services-grid-heading"
        eyebrow={s.services_section_eyebrow}
        title={s.services_section_heading}
        text={s.services_section_text}
      />
      <div className="mt-14 hidden gap-7 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {services.map((category, i) => (
          <ServiceCard key={category.id} category={category} index={i} />
        ))}
      </div>
      <div className="mt-12">
        <MobileCarousel testid="services-carousel">
          {services.map((category, i) => (
            <ServiceCard key={category.id} category={category} index={i} />
          ))}
        </MobileCarousel>
      </div>
    </div>
  </section>
);

const WhatWeDoCard = ({ item, index }) => (
  <motion.article
    {...fadeUp((index % 3) * 0.1)}
    data-testid={`what-we-do-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className="group overflow-hidden rounded-2xl bg-white shadow-[0_6px_24px_rgba(0,0,0,0.06)] ring-1 ring-[#F0F0F0] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_45px_rgba(198,40,40,0.12)]"
  >
    <div className="relative h-52 overflow-hidden">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-[#1F1F1F]/0 transition-colors duration-300 group-hover:bg-[#C62828]/15" />
    </div>
    <div className="p-7">
      <h3 className="text-lg font-extrabold tracking-wide text-[#1F1F1F]">{item.title}</h3>
      <span className="mt-3 block h-[3px] w-10 rounded-full bg-[#C62828] transition-all duration-300 group-hover:w-16" />
      <p className="mt-4 text-sm leading-relaxed text-[#666666]">{item.text}</p>
    </div>
  </motion.article>
);

const WhatWeDo = ({ s, items }) => (
  <section data-testid="what-we-do-section" className="bg-white py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        testid="what-we-do-heading"
        eyebrow={s.whatwedo_eyebrow}
        title={s.whatwedo_heading}
        text={s.whatwedo_text}
      />
      <div className="mt-14 hidden gap-7 sm:grid sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => (
          <WhatWeDoCard key={item.id || item.title} item={item} index={i} />
        ))}
      </div>
      <div className="mt-12">
        <MobileCarousel testid="whatwedo-carousel">
          {items.map((item, i) => (
            <WhatWeDoCard key={item.id || item.title} item={item} index={i} />
          ))}
        </MobileCarousel>
      </div>
    </div>
  </section>
);

const WhyChooseUs = ({ s, items }) => (
  <section data-testid="why-choose-section" className="bg-[#F8F8F8] py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        testid="why-choose-heading"
        eyebrow={s.whychoose_eyebrow}
        title={s.whychoose_heading}
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => {
          const Icon = WHY_ICONS[i % WHY_ICONS.length];
          return (
            <motion.div
              key={item.id || item.title}
              {...fadeUp((i % 3) * 0.1)}
              data-testid={`why-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group flex gap-5 rounded-2xl border border-[#F0F0F0] bg-white p-7 shadow-[0_6px_24px_rgba(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_16px_40px_rgba(198,40,40,0.1)]"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#C62828]/10 text-[#C62828] transition-colors duration-300 group-hover:bg-[#C62828] group-hover:text-white">
                <Icon size={24} strokeWidth={2} />
              </span>
              <div>
                <h3 className="text-[15px] font-extrabold tracking-wide text-[#1F1F1F]">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#666666]">{item.text}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const Approach = ({ s, steps }) => (
  <section data-testid="approach-section" className="bg-[#1F1F1F] py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading testid="approach-heading" eyebrow={s.approach_eyebrow} title={s.approach_heading} dark />
      <div className="relative mt-16">
        <div className="absolute left-6 top-0 h-full w-px bg-white/15 lg:left-0 lg:top-7 lg:h-px lg:w-full" />
        <div className="grid gap-10 lg:grid-cols-5 lg:gap-6">
          {steps.map((step, i) => {
            const num = step.step || String(i + 1).padStart(2, "0");
            return (
              <motion.div
                key={step.id || num}
                {...fadeUp(i * 0.12)}
                data-testid={`approach-step-${num}`}
                className="relative flex gap-5 pl-1 lg:flex-col lg:items-center lg:gap-0 lg:pl-0 lg:text-center"
              >
                <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C62828] text-sm font-extrabold text-white shadow-[0_0_0_8px_#1F1F1F,0_10px_25px_rgba(198,40,40,0.4)]">
                  {num}
                </span>
                <div className="lg:mt-6">
                  <h3 className="text-base font-extrabold uppercase tracking-wider text-white">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#B5B5B5]">{step.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

const CTA = ({ s }) => (
  <section data-testid="cta-section" className="bg-[#C62828] py-20 lg:py-24">
    <div className="mx-auto max-w-4xl px-5 text-center">
      <motion.div {...fadeUp()}>
        <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          {s.cta_heading}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-white/85 sm:text-lg">{s.cta_text}</p>
        <a
          data-testid="cta-plan-event-button"
          href={s.contact_url}
          className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#C62828] shadow-[0_14px_35px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#1F1F1F] hover:text-white"
        >
          {s.cta_button}
          <ArrowRight size={17} strokeWidth={2.5} />
        </a>
      </motion.div>
    </div>
  </section>
);

const Services = () => {
  const s = useSiteContent();
  const servicesRaw = useWpCollection("services", SERVICE_CATEGORIES);
  const whatWeDo = useWpCollection("what-we-do", WHAT_WE_DO);
  const whyChoose = useWpCollection("why-choose-us", WHY_CHOOSE);
  const approach = useWpCollection("approach", APPROACH_STEPS);
  const services = servicesRaw.map(toService);

  return (
    <div data-testid="services-page" className="bg-white">
      <Header />
      <main>
        <Hero s={s} />
        <Intro s={s} />
        <ServicesGrid s={s} services={services} />
        <WhatWeDo s={s} items={whatWeDo} />
        <WhyChooseUs s={s} items={whyChoose} />
        <Approach s={s} steps={approach} />
        <CTA s={s} />
      </main>
      <Footer />
    </div>
  );
};

export default Services;
